import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';
import { useWorkspace } from '@/contexts/WorkspaceContext';

type IntegrationConfig = Database['public']['Tables']['integration_configs']['Row'];

export const useIntegrationConfigs = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  // Get integration config by type
  const getIntegrationConfig = (integrationType: string) => {
    return useQuery({
      queryKey: ['integration-config', integrationType],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('integration_configs')
          .select('*')
          .eq('integration_type', integrationType)
          .maybeSingle();

        if (error) throw error;
        return data;
      },
    });
  };

  // Save/update integration config
  const saveIntegrationConfig = useMutation({
    mutationFn: async (config: {
      integration_type: string;
      integration_name: string;
      provider: string;
      credentials: any;
      is_enabled?: boolean;
    }) => {
      // Get user authentication
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use current workspace from context
      if (!currentWorkspace?.id) {
        throw new Error('No workspace selected. Please select a workspace first.');
      }

      const workspaceId = currentWorkspace.id;

      const { data, error } = await supabase
        .from('integration_configs')
        .upsert(
          [{
            workspace_id: workspaceId,
            integration_type: config.integration_type,
            integration_name: config.integration_name,
            provider: config.provider,
            credentials: config.credentials,
            is_enabled: config.is_enabled ?? true,
          }],
          {
            onConflict: 'workspace_id,integration_type',
          }
        )
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integration-config', variables.integration_type] });
      toast.success('Integration configuration saved');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save configuration');
    },
  });

  // Test connection
  const testConnection = useMutation({
    mutationFn: async (params: {
      integrationType: string;
      apiBaseUrl: string;
      bearerToken?: string;
      workspaceId?: string;
    }) => {
      const workspaceIdToUse = params.workspaceId || currentWorkspace?.id;
      
      if (!workspaceIdToUse) {
        throw new Error('No workspace selected. Please select a workspace first.');
      }

      const { data, error } = await supabase.functions.invoke('parser-bot-integration', {
        body: {
          action: 'test_connection',
          apiBaseUrl: params.apiBaseUrl,
          bearerToken: params.bearerToken || null,
          workspaceId: workspaceIdToUse,
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Connection test failed');
      
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integration-config', variables.integrationType] });
      toast.success(data.message || 'Connection successful');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Connection test failed');
    },
  });

  // Sync suppliers (for parser_bot)
  const syncSuppliers = useMutation({
    mutationFn: async (workspaceId?: string) => {
      const workspaceIdToUse = workspaceId || currentWorkspace?.id;
      
      if (!workspaceIdToUse) {
        throw new Error('No workspace selected. Please select a workspace first.');
      }

      const { data, error } = await supabase.functions.invoke('parser-bot-integration', {
        body: { 
          action: 'sync_suppliers',
          workspaceId: workspaceIdToUse
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to sync suppliers');
      
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integration-config'] });
      queryClient.invalidateQueries({ queryKey: ['parser-bot-suppliers'] });
      toast.success(data.message || 'Suppliers synced successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to sync suppliers');
    },
  });

  return {
    getIntegrationConfig,
    saveIntegrationConfig,
    testConnection,
    syncSuppliers,
  };
};
